import { GroupPromoCards } from '@molecules/GroupPromoCards';
import TicketCard from '@molecules/TicketCard';
import {
  QueryKeys,
  useGetBulkEvents,
  useGetBulkGroupPromosByCommunities,
  useGetUserTicketsTemporal,
} from '@sunrise-ui/api-client';
import type { Event, Ticket } from '@sunrise-ui/api/events';
import { TemporalRelation } from '@sunrise-ui/api/events';
import { TicketReceiptModal } from '@sunrise-ui/composites';
import {
  CARD_MARGIN,
  CacheAvailabilityProvider,
  Loader,
  TextBold,
  useAuthStore,
  useDynamicStyles,
  useMediaQueries,
} from '@sunrise-ui/primitives';
import type { ReactElement } from 'react';
import React, { useMemo, useState } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';

const { eventsKey } = QueryKeys.events;

type Props = {
  userId?: string;
  ListHeaderComponent?: ReactElement;
  temporalRelation?: TemporalRelation;
};

export default function UserTicketsList({
  userId,
  ListHeaderComponent,
  temporalRelation = TemporalRelation.Future,
}: Props) {
  const { currentUserId } = useAuthStore();
  const isSelf = currentUserId === userId;
  const { isMobile } = useMediaQueries();
  const styles = useDynamicStyles(createStyles);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<{
    ticket: Ticket;
    event: Event;
  } | null>(null);

  const {
    data: tickets,
    isLoading: isTicketsLoading,
    refetch: refetchTickets,
  } = useGetUserTicketsTemporal({ relation: temporalRelation, userId });

  // Extract unique event IDs from active tickets
  const eventIds = useMemo(
    () =>
      tickets
        ?.filter((ticket) => ticket.is_active)
        .map((ticket) => ticket.event_id)
        .filter((id, index, arr) => arr.indexOf(id) === index),
    [tickets],
  );

  const { data: events, isLoading: isLoadingEvents } =
    useGetBulkEvents(eventIds);

  const communityIds = useMemo(
    () =>
      events ? [...new Set(events.map((e) => e.community_id))] : undefined,
    [events],
  );

  const { data: groupPromos, isLoading: isLoadingGroupPromos } =
    useGetBulkGroupPromosByCommunities(communityIds);

  // Filter for only internally ticketed events (exclude external ticketing)
  const internalEvents = useMemo(
    () =>
      events?.filter(
        (event) =>
          !event.ticketing_platform_url &&
          tickets?.some(
            (ticket) =>
              ticket.event_id === event.id &&
              ticket.is_active &&
              ticket.user_id === userId,
          ),
      ),
    [events, tickets, userId],
  );

  // Create ticket-event pairs for rendering
  const ticketEventPairs = useMemo(() => {
    if (!internalEvents || !tickets) return [];

    return internalEvents
      .map((event) => {
        const ticket = tickets.find(
          (t) => t.event_id === event.id && t.is_active && t.user_id === userId,
        );
        return ticket ? { ticket, event } : null;
      })
      .filter((pair) => pair !== null)
      .sort(
        (a, b) =>
          new Date(a!.event.start_date!).getTime() -
          new Date(b!.event.start_date!).getTime(),
      ) as Array<{ ticket: Ticket; event: Event }>;
  }, [internalEvents, tickets, userId]);

  const isLoading =
    !isRefreshing &&
    (isTicketsLoading || isLoadingEvents || isLoadingGroupPromos);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchTickets();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleViewTicket = (ticket: Ticket, event: Event) => {
    setSelectedTicket({ ticket, event });
  };

  const renderTicketCard = ({
    item,
    index,
  }: {
    item: { ticket: Ticket; event: Event };
    index: number;
  }) => {
    const isLastItem = index === ticketEventPairs.length - 1;
    const shouldAddPlaceholder =
      !isMobile && isLastItem && ticketEventPairs.length % 2 !== 0;

    return (
      <View style={styles.row}>
        <TicketCard
          ticket={item.ticket}
          event={item.event}
          userId={userId}
          onViewTicket={() => handleViewTicket(item.ticket, item.event)}
          style={styles.card}
        />
        {shouldAddPlaceholder && <View style={styles.card} />}
      </View>
    );
  };

  if (isLoading) {
    return (
      <>
        {ListHeaderComponent}
        <Loader style={styles.loader} />
      </>
    );
  }

  return (
    <CacheAvailabilityProvider queryKeys={new Set([[eventsKey]])}>
      <FlatList
        data={ticketEventPairs}
        renderItem={renderTicketCard}
        keyExtractor={(item) => item.ticket.id!}
        onRefresh={handleRefresh}
        refreshing={isRefreshing}
        ListHeaderComponent={
          <>
            {ListHeaderComponent}
            <GroupPromoCards groupPromos={groupPromos} />
          </>
        }
        ListEmptyComponent={
          <TextBold style={styles.emptyStateText}>
            {isSelf ? 'You have' : 'This user has'} no{' '}
            {temporalRelation === TemporalRelation.Past ? 'past' : 'upcoming'}{' '}
            tickets
          </TextBold>
        }
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
      />

      <TicketReceiptModal
        visible={!!selectedTicket}
        onDismiss={() => setSelectedTicket(null)}
        ticket={selectedTicket?.ticket}
        event={selectedTicket?.event}
      />
    </CacheAvailabilityProvider>
  );
}

const createStyles = () =>
  StyleSheet.create({
    row: {
      flexDirection: 'row',
      justifyContent: 'center',
    },
    card: {
      marginVertical: CARD_MARGIN,
      marginHorizontal: CARD_MARGIN,
    },
    listContent: {
      paddingBottom: 100,
    },
    emptyStateText: {
      textAlign: 'center',
      marginTop: 50,
      fontSize: 16,
    },
    loader: {
      marginTop: 50,
    },
  });
